import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';
import { WidgetHostMessage } from './WidgetHostMessage';

interface WidgetErrorBoundaryProps extends WithTranslation {
  widgetKey?: string;
  children: ReactNode;
}

interface WidgetErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
}

/**
 * Catches render errors inside individual widgets so a single broken widget
 * does not crash the entire host page or neighbouring widgets.
 */
class WidgetErrorBoundaryInner extends Component<
  WidgetErrorBoundaryProps,
  WidgetErrorBoundaryState
> {
  constructor(props: WidgetErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): WidgetErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error.message || String(error),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `[wa] Widget "${this.props.widgetKey ?? 'unknown'}" crashed:`,
      error,
      info.componentStack,
    );
  }

  render(): ReactNode {
    const { t, widgetKey, children } = this.props;

    if (this.state.hasError) {
      return (
        <WidgetHostMessage
          variant='error'
          title={t('embeddableWidget.host.renderFailedTitle')}
          message={t('embeddableWidget.host.renderFailedMessage')}
          detail={
            widgetKey
              ? `${widgetKey}: ${this.state.errorMessage}`
              : this.state.errorMessage
          }
        />
      );
    }

    return children;
  }
}

export const WidgetErrorBoundary = withTranslation()(WidgetErrorBoundaryInner);
